/**
 * Sprint Create/Edit Modal
 */
import { useState, useEffect } from 'react';
import { X, Calendar, Target, Users } from 'lucide-react';
import { Sprint, CreateSprintData } from '@/types';
import { sprintAPI } from '@/api/sprintAPI';
import toast from 'react-hot-toast';
import { Loader } from './Loader';
import React from 'react'; // ensure React import stays

interface SprintModalProps {
  projectId: string;
  sprint?: Sprint | null;
  onClose: () => void;
  onSuccess: () => void;
}

export function SprintModal({ projectId, sprint, onClose, onSuccess }: SprintModalProps) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    goal: '',
    start_date: '',
    end_date: '',
    capacity: '',
  });

  // Form field validation errors
  const [formErrors, setFormErrors] = useState<{ name?: string; description?: string; start_date?: string; end_date?: string }>({});

  useEffect(() => {
    if (sprint) {
      setFormData({
        name: sprint.name,
        description: sprint.description || '',
        goal: sprint.goal || '',
        start_date: sprint.start_date ? sprint.start_date.split('T')[0] : '',
        end_date: sprint.end_date ? sprint.end_date.split('T')[0] : '',
        capacity: sprint.capacity?.toString() || '',
      });
    }
  }, [sprint]);

  const validateForm = () => {
    const errors: { name?: string; description?: string; start_date?: string; end_date?: string } = {};
    const { name, description, start_date, end_date } = formData;

    if (!name || !name.trim()) {
      errors.name = 'Sprint name is required';
    }

    // description validation: required and min length 10
    if (!description || !description.trim()) {
      errors.description = 'Description is required';
    } else if (description.trim().length < 10) {
      errors.description = 'Description must be at least 10 characters';
    }

    if (!start_date) {
      errors.start_date = 'Start date is required';
    }
    if (!end_date) {
      errors.end_date = 'End date is required';
    }

    if (start_date && end_date) {
      const s = new Date(start_date);
      const e = new Date(end_date);
      s.setHours(0, 0, 0, 0);
      e.setHours(0, 0, 0, 0);
      if (e < s) {
        errors.end_date = 'End date must be the same or after start date';
      }
    }

    // Start date must not be in the past
    if (start_date) {
      const s = new Date(start_date);
      const today = new Date();
      s.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);
      if (s < today) {
        errors.start_date = 'Start date cannot be in the past';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // validate form before submitting
    if (!validateForm()) {
      setLoading(false);
      return;
    }

    try {
      const toIsoUtc = (d?: string, endOfDay = false) => {
        if (!d) return undefined as any;
        // Construct explicit UTC ISO string to satisfy servers requiring datetime
        return endOfDay ? `${d}T23:59:59Z` : `${d}T00:00:00Z`;
      };
      const data: any = {
        name: formData.name,
        description: formData.description || undefined,
        goal: formData.goal || undefined,
        start_date: toIsoUtc(formData.start_date),
        end_date: toIsoUtc(formData.end_date, true),
        capacity: formData.capacity !== '' ? Math.max(0, parseInt(formData.capacity, 10)) : undefined,
      };

      if (sprint) {
        await sprintAPI.updateSprint(sprint.id, data);
        toast.success('Sprint updated successfully');
      } else {
        data.project_id = projectId;
        await sprintAPI.createSprint(data as CreateSprintData);
        toast.success('Sprint created successfully');
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      const apiDetail = error?.response?.data?.detail;
      // Pydantic-style validation error normalization (UAT)
      const firstFieldError = Array.isArray(apiDetail) && apiDetail.length > 0
        ? `${apiDetail[0]?.loc?.join('.')}: ${apiDetail[0]?.msg}`
        : (typeof apiDetail === 'string' ? apiDetail : 'Failed to save sprint');
      console.error('Create sprint failed. Payload:', formData, 'Error:', error?.response?.data || error);
      toast.error(firstFieldError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            {sprint ? 'Edit Sprint' : 'Create Sprint'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            data-testid="close-sprint-modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Sprint Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sprint Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => {
                setFormData({ ...formData, name: e.target.value });
                setFormErrors(prev => ({ ...prev, name: undefined }));
              }}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${formErrors.name ? 'border-red-400' : 'border-gray-300'}`}
              placeholder="e.g., Sprint 1, Q1 Sprint"
              data-testid="sprint-name-input"
            />
            {formErrors.name && <p className="mt-1 text-xs text-red-600">{formErrors.name}</p>}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => {
                setFormData({ ...formData, description: e.target.value });
                setFormErrors(prev => ({ ...prev, description: undefined }));
              }}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${formErrors.description ? 'border-red-400' : 'border-gray-300'}`}
              rows={3}
              placeholder="Sprint description"
              data-testid="sprint-description-input"
            />
            {formErrors.description && <p className="mt-1 text-xs text-red-600">{formErrors.description}</p>}
          </div>

          {/* Sprint Goal */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Target className="w-4 h-4 inline mr-1" />
              Sprint Goal
            </label>
            <input
              type="text"
              value={formData.goal}
              onChange={(e) => setFormData({ ...formData, goal: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="What do you want to achieve in this sprint?"
              data-testid="sprint-goal-input"
            />
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                Start Date *
              </label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => {
                  setFormData({ ...formData, start_date: e.target.value });
                  // clear or revalidate on change
                  setFormErrors(prev => ({ ...prev, start_date: undefined, end_date: undefined }));
                }}
                min={new Date().toISOString().split('T')[0]}
                className={`w-full px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${formErrors.start_date ? 'border-red-400' : 'border-gray-300'}`}
                data-testid="sprint-start-date-input"
              />
              {formErrors.start_date && <p className="mt-1 text-xs text-red-600">{formErrors.start_date}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                End Date *
              </label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => {
                  setFormData({ ...formData, end_date: e.target.value });
                  // clear or revalidate on change
                  setFormErrors(prev => ({ ...prev, end_date: undefined }));
                }}
                className={`w-full px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${formErrors.end_date ? 'border-red-400' : 'border-gray-300'}`}
                data-testid="sprint-end-date-input"
              />
              {formErrors.end_date && <p className="mt-1 text-xs text-red-600">{formErrors.end_date}</p>}
            </div>
          </div>

          {/* Capacity */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Users className="w-4 h-4 inline mr-1" />
              Capacity (Story Points)
            </label>
            <input
              type="number"
              value={formData.capacity}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === '') { setFormData({ ...formData, capacity: '' }); return; }
                const n = Number(raw);
                if (Number.isNaN(n)) return;
                const clamped = Math.max(0, Math.floor(n));
                setFormData({ ...formData, capacity: String(clamped) });
              }}
              onKeyDown={(e) => {
                // Disallow characters that can make the value negative or non-integer
                if (['e','E','+','-','.'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="20"
              min="0"
              step="1"
              inputMode="numeric"
              pattern="[0-9]*"
              data-testid="sprint-capacity-input"
            />
            <p className="mt-1 text-sm text-gray-500">
              Total story points the team can complete in this sprint
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              data-testid="cancel-sprint-button"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              data-testid="save-sprint-button"
            >
              {loading ? <Loader variant="overlay" size="xl" text="Saving..." />: sprint ? 'Update Sprint' : 'Create Sprint'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


